import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'react-bootstrap';
import { observer, useLocalStore } from 'mobx-react';
import SVG from 'react-inlinesvg';
import { Link } from 'react-mobx-router5';

import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import CollapseIcon from 'img/icons/web-icon-caret-thin-up.svg';
import ExpandIcon from 'img/icons/web-icon-caret-thin-down.svg';

import { storesContext } from 'stores';
import { FeatureFlag } from '@ole-ui/ole-ui-components';
import ComponentError from '../Error/ComponentError';
import VersionCard from './VersionCard';
import './styles.scss';
import Store from './store';

interface MyProgressWidgetProps {
  collapsible?: boolean;
  allowShowMore?: boolean;
}

const MyProgressWidget = ({
  collapsible = true,
  allowShowMore = true,
}: MyProgressWidgetProps) => {
  const { t } = useTranslation();
  const { progressStore, catalogStore } = useContext(storesContext);
  const store = useLocalStore(() => new Store());

  const visibleOfferings = progressStore.visibleOfferingsWithProgress;

  const displayedOfferings = useMemo(
    () => visibleOfferings.slice(0, store.visibleItems),
    [visibleOfferings, store.visibleItems],
  );

  const hasMoreItems = visibleOfferings.length > store.visibleItems;

  const renderContent = () => {
    if (!displayedOfferings.length) {
      return (
        <div className="no-progress-msg" data-testid="no-progress-message">
          <p>{t('There are no courses currently in progress.')}</p>
          <Link href routeName="catalog">
            {t('Explore the Catalog')}
          </Link>
        </div>
      );
    }

    return displayedOfferings.map(({ entry, progress }) => {
      const allVersions = catalogStore.getCatalogEntriesForCode(entry.code);

      // Skip if no catalog entries found (edge case)
      if (Object.keys(allVersions).length === 0) {
        return null;
      }

      return (
        <VersionCard
          key={entry.slug || progress?.course_slug}
          catalogEntries={allVersions}
          courseSlug={entry.slug || progress?.course_slug}
          courseCode={entry.code}
        />
      );
    });
  };

  return (
    <FeatureFlag section="my_widgets" flagName="my_progress" defaultFlagValue>
      <Panel
        className="my-progress-widget"
        expanded={store.opened}
        onToggle={store.toggle}
        data-analytics-id="my-progress-widget-lp"
        data-testid="my-progress-widget"
      >
        {collapsible && (
          <Panel.Heading>
            <Panel.Title toggle>
              {t('MY PROGRESS')}
              <div className="my-progress-widget-expand">
                <SVG src={store.opened ? CollapseIcon : ExpandIcon}>
                  {store.opened ? t('Collapse') : t('Expand')}
                </SVG>
              </div>
            </Panel.Title>
          </Panel.Heading>
        )}
        <Panel.Collapse>
          <Panel.Body>{renderContent()}</Panel.Body>
          {allowShowMore && hasMoreItems && (
            <Panel.Footer
              onClick={() => store.showMoreItems()}
              data-analytics-id="show-more-my-progress-widget-lp"
              data-testid="show-more-button"
            >
              {t('Show more')}
            </Panel.Footer>
          )}
        </Panel.Collapse>
      </Panel>
    </FeatureFlag>
  );
};

export default withErrorBoundary(
  observer(MyProgressWidget),
  ComponentError,
  handleError,
);
